/* eslint-disable react-hooks/exhaustive-deps */
import axios from "axios";
import {
  Calendar,
  Clock,
  MapPin,
  MoreVertical,
  Package,
  Phone,
  Scissors,
} from "lucide-react";
import { useEffect, useState } from "react";
import { CgSpinner } from "react-icons/cg";
import { useNavigate } from "react-router-dom";
import RescheduleBookingModal from "../../../components/AdminSideComponents/bookings/RescheduleBookingModal";
import ConfirmCancelModal from "../../../components/StaffSideComponents/myJobs/ConfirmCancelModal";
import JobActionModal from "../../../components/StaffSideComponents/myJobs/JobActionModal";
import VerifyOTPModal from "../../../components/StaffSideComponents/myJobs/VerifyOTPModal";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import {
  formatDateTime,
  getTimeInAmPmFormat,
} from "../../../utils/formatDateTime";
import { formatDate } from "../../../utils/helpers";
import {
  confirmAction,
  showError,
  showSuccess,
} from "../../../utils/toastUtils";

export const ONGOING_GRACE_MS = 5 * 60 * 1000;
export const GRACE_STORAGE_KEY = "job-ongoing-grace";

const todayDate = new Date().toLocaleDateString("en-US", {
  weekday: "long",
  month: "short",
  day: "numeric",
});

export const statusConfig = {
  verified: {
    label: "Assigned",
    color: "bg-blue-50 text-blue-700 border-blue-200",
    action: "Accept Job",
    btnColor: "bg-blue-600 active:bg-blue-700",
  },
  assigned: {
    label: "Assigned",
    color: "bg-blue-50 text-blue-700 border-blue-200",
    action: "Accept Job",
    btnColor: "bg-blue-600 active:bg-blue-700",
  },
  confirmed: {
    label: "Accepted",
    color: "bg-amber-50 text-amber-700 border-amber-200",
    action: "Start Work",
    btnColor: "bg-amber-600 active:bg-amber-700",
  },
  ongoing: {
    label: "Ongoing",
    color: "bg-green-50 text-green-700 border-green-200",
    action: "Complete Job",
    btnColor: "bg-green-600 active:bg-green-700",
  },
  completed: {
    label: "Completed",
    color: "bg-gray-200 text-gray-600 border-gray-300",
    action: null,
    btnColor: "bg-gray-600 active:bg-gray-700",
  },
  cancelled: {
    label: "Cancelled",
    color: "bg-red-50 text-red-600 border-red-200",
    action: null,
    btnColor: "bg-red-500",
  },
  rescheduled: {
    label: "Rescheduled",
    color: "bg-purple-50 text-purple-700 border-purple-200",
    action: "Start Work",
    btnColor: "bg-purple-600 active:bg-purple-700",
  },
};

export const statusConfirmationMap = {
  assigned: {
    confirmed: {
      title: "Accept Job?",
      text: "Do you want to accept this job and confirm your availability?",
      confirmText: "Yes, Accept Job",
      cancelText: "Not Now",
      icon: "question",
    },
  },
  confirmed: {
    ongoing: {
      title: "Start Work?",
      text: "Please start work only when you are at the customer location.",
      confirmText: "Yes, Start Work",
      cancelText: "Not Yet",
      icon: "warning",
    },
  },
  rescheduled: {
    ongoing: {
      title: "Start Work?",
      text: "This job was rescheduled. Start work only when you reach the location.",
      confirmText: "Yes, Start Work",
      cancelText: "Not Yet",
      icon: "warning",
    },
  },
  ongoing: {
    completed: {
      title: "Complete Work?",
      text: "Are you sure the work has been completed successfully?",
      confirmText: "Yes, Complete",
      cancelText: "Not Yet",
      icon: "question",
    },
  },
};

export const getConfirmationConfig = (currentStatus, nextStatus) => {
  return statusConfirmationMap?.[currentStatus]?.[nextStatus] || null;
};

export const getNextStatus = (current) => {
  const flow = {
    assigned: "confirmed",
    confirmed: "ongoing",
    rescheduled: "ongoing",
    ongoing: "completed",
  };
  return flow[current] || null;
};

export const getGraceMap = () => {
  try {
    return JSON.parse(localStorage.getItem(GRACE_STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};

export const setGraceMap = (map) => {
  localStorage.setItem(GRACE_STORAGE_KEY, JSON.stringify(map));
};

// ── shared transform helper ──────────────────────────────────────────────────
const transformToJob = (booking, bookingType) => {
  const isPackage = bookingType === "package";

  const effectiveDate = booking.rescheduledDate || booking.slotAvailabilityId?.date;
  const slot = booking?.slotSnapshot
    ? `${getTimeInAmPmFormat(booking.slotSnapshot.startTime)} - ${getTimeInAmPmFormat(booking.slotSnapshot.endTime)}`
    : null;

  const locationAddress = booking.location?.address;
  const shortLocation = locationAddress
    ? `${locationAddress.locality}, ${locationAddress.city}`
    : null;
  const coordinates = booking.location?.coordinates;
  const mapLink =
    coordinates?.length === 2
      ? `https://www.google.com/maps?q=${coordinates[1]},${coordinates[0]}`
      : null;

  // Service label differs per type
  const serviceLabel = isPackage
    ? booking.packageSnapshot?.name || "Package"
    : booking.services?.[0]?.serviceId?.name || "Service";

  const serviceCount = isPackage
    ? (booking.packageSnapshot?.services || []).length
    : booking.services?.length || 0;

  return {
    id: booking._id,
    raw: booking,
    bookingType, // "service" | "package"

    branchId: booking?.branchId?._id || booking?.branchId,
    customerName: booking.userId?.name || "N/A",
    phoneNumber: booking.userId?.phoneNumber || "",

    service: serviceLabel,
    serviceCount,
    duration: !isPackage ? booking.services?.[0]?.serviceId?.duration || "" : "",

    totalAmount: booking.finalPayableAmount || booking.totalAmount || 0,

    displayDate: formatDate(effectiveDate),
    createdAt: formatDateTime(booking.createdAt),
    timeSlot: slot,
    rescheduledDate: booking.rescheduledDate,

    location: shortLocation,
    mapLink,

    status: booking.status,
    rawStatus: booking.status,
  };
};

// ────────────────────────────────────────────────────────────────────────────
const StaffMyJobs = () => {
  const navigate = useNavigate();
  const { admin } = useAdminAuth();

  const [selectedJob, setSelectedJob] = useState(null);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loading, setIsLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otpJob, setOtpJob] = useState(null); // { id, bookingType }

  // ── Fetch both service + package bookings for this staff ──────────────────
  const fetchBookings = async () => {
    if (!admin?.id) return;
    setIsLoading(true);
    try {
      const [serviceRes, packageRes] = await Promise.allSettled([
        axios.get(
          `${process.env.REACT_APP_API_URL}/service-bookings/get-all/by-staff-id/${admin.id}`,
        ),
        axios.get(
          `${process.env.REACT_APP_API_URL}/package-bookings/staff/${admin.id}`,
        ),
      ]);

      const serviceJobs =
        serviceRes.status === "fulfilled"
          ? (serviceRes.value.data?.data || []).map((b) => transformToJob(b, "service"))
          : [];

      const packageJobs =
        packageRes.status === "fulfilled"
          ? (packageRes.value.data?.data || []).map((b) => transformToJob(b, "package"))
          : [];

      // Merge + sort newest first
      const merged = [...serviceJobs, ...packageJobs].sort(
        (a, b) => new Date(b.raw.createdAt) - new Date(a.raw.createdAt),
      );

      setBookings(merged);
    } catch (error) {
      console.error("Failed to fetch bookings", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (admin?.id) fetchBookings();
  }, [admin?.id]);

  // ── Grace period helpers ───────────────────────────────────────────────────
  const canUndoOngoing = (jobId, status) => {
    if (status !== "ongoing") return false;
    const graceMap = getGraceMap();
    const startedAt = graceMap[jobId];
    if (!startedAt) return false;
    return Date.now() - startedAt <= ONGOING_GRACE_MS;
  };

  // ── Status update endpoint switcher ───────────────────────────────────────
  const getStatusUpdateUrl = (bookingType) =>
    bookingType === "package"
      ? `${process.env.REACT_APP_API_URL}/package-bookings/update-status`
      : `${process.env.REACT_APP_API_URL}/staff-assignment/update-status`;

  // ── Main action handler ────────────────────────────────────────────────────
  const handleAction = async (jobId, currentStatus, bookingType) => {
    if (updatingId === jobId) return;

    const nextStatus = getNextStatus(currentStatus);
    if (!nextStatus) return;

    // Show OTP modal before going ongoing
    if (
      (currentStatus === "confirmed" || currentStatus === "rescheduled") &&
      nextStatus === "ongoing"
    ) {
      try {
        setUpdatingId(jobId);

        // Always generate OTP when starting work
        await axios.post(
          `${process.env.REACT_APP_API_URL}/staff-assignment/generate-start-otp/${jobId}`
        );

        showSuccess("OTP sent to customer");

        setOtpJob({ id: jobId, bookingType });
        setShowOTPModal(true);

      } catch (error) {
        showError(error.response?.data?.message || "Failed to send OTP");
      } finally {
        setUpdatingId(null);
      }

      return;
    }

    const confirmationConfig = getConfirmationConfig(currentStatus, nextStatus);
    if (confirmationConfig) {
      const confirmed = await confirmAction(confirmationConfig);
      if (!confirmed) return;
    }

    setUpdatingId(jobId);
    try {
      await axios.put(getStatusUpdateUrl(bookingType), {
        bookingId: jobId,
        status: nextStatus,
      });

      if (nextStatus === "ongoing") {
        const graceMap = getGraceMap();
        graceMap[jobId] = Date.now();
        setGraceMap(graceMap);
      }

      setBookings((prev) =>
        prev.map((job) => (job.id === jobId ? { ...job, status: nextStatus } : job)),
      );
      showSuccess("Status Updated");
    } catch (error) {
      console.error("Failed to update status", error);
      showError(error?.response?.data?.message || "Failed to update status");
    } finally {
      setUpdatingId(null);
    }
  };

  // ── Called after OTP verified ──────────────────────────────────────────────
  const continueStartWork = async () => {
    if (!otpJob) return;
    const { id: jobId, bookingType } = otpJob;

    try {
      await axios.put(getStatusUpdateUrl(bookingType), {
        bookingId: jobId,
        status: "ongoing",
      });

      const graceMap = getGraceMap();
      graceMap[jobId] = Date.now();
      setGraceMap(graceMap);

      setBookings((prev) =>
        prev.map((job) => (job.id === jobId ? { ...job, status: "ongoing" } : job)),
      );
      showSuccess("Work started");
    } catch (error) {
      showError("Failed to start work");
    } finally {
      setShowOTPModal(false);
      setOtpJob(null);
    }
  };

  // ── Undo start work ────────────────────────────────────────────────────────
  const undoStartWork = async (jobId) => {
    if (!jobId) return;
    const job = bookings.find((b) => b.id === jobId);
    if (!job) return;

    const graceMap = getGraceMap();
    delete graceMap[jobId];
    setGraceMap(graceMap);

    try {
      await axios.put(getStatusUpdateUrl(job.bookingType), {
        bookingId: jobId,
        status: "confirmed",
      });
      setBookings((prev) =>
        prev.map((b) => (b.id === jobId ? { ...b, status: "confirmed" } : b)),
      );
      showSuccess("Start work undone");
    } catch (error) {
      console.error("Failed to undo", error);
    } finally {
      setUpdatingId(null);
      setShowActionSheet(false);
    }
  };

  // ── Mark staff unavailable ─────────────────────────────────────────────────
  const handleCancelJob = async (jobId, reason, branchId) => {
    if (!jobId || !admin?.id) return;
    const job = bookings.find((b) => b.id === jobId);
    if (!job) return;

    setShowActionSheet(false);
    setIsCancelling(true);
    try {
      if (job.bookingType === "package") {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/package-bookings/update-status`,
          {
            bookingId: jobId,
            action: "staff_unavailable",
            staffId: admin.id,
            cancelReason: reason,
          },
        );
      } else {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/service-bookings/update/${jobId}`,
          {
            branchId,
            action: "staff_unavailable",
            staffId: admin.id,
            cancelReason: reason,
          },
        );
      }

      setBookings((prev) => prev.filter((b) => b.id !== jobId));
      showSuccess("Marked unavailable.");
    } catch (error) {
      console.error("Failed to mark unavailable", error);
      showError(error?.response?.data?.message || "Failed to mark unavailable");
    } finally {
      setIsCancelling(false);
      setShowCancelConfirm(false);
    }
  };

  // ── Reschedule success ─────────────────────────────────────────────────────
  const handleRescheduleSuccess = (updatedBooking) => {
    setBookings((prev) =>
      prev.map((job) => {
        if (job.id !== updatedBooking._id) return job;
        const slot = updatedBooking?.slotSnapshot
          ? `${getTimeInAmPmFormat(updatedBooking.slotSnapshot.startTime)} - ${getTimeInAmPmFormat(updatedBooking.slotSnapshot.endTime)}`
          : null;
        return {
          ...job,
          status: updatedBooking?.status,
          displayDate: formatDate(updatedBooking?.rescheduledDate),
          timeSlot: slot,
        };
      }),
    );
    setShowRescheduleModal(false);
  };

  // ── Job Card ───────────────────────────────────────────────────────────────
  const JobCard = ({ job }) => {
    const config = statusConfig[job.status];
    const showThreeDot = !["cancelled", "completed", "ongoing"].includes(job.status);
    const isPackage = job.bookingType === "package";

    const jobInfoItems = [
      {
        key: "service",
        icon: isPackage ? Package : Scissors,
        show: !!job.service,
        primary: () => job.service,
        secondary: () =>
          job.serviceCount > 1 ? `+${job.serviceCount - 1} more` : null,
      },
      {
        key: "time",
        icon: Clock,
        show: !!job.displayDate || !!job.timeSlot,
        primary: () => job.displayDate,
        secondary: () => job.timeSlot,
      },
      {
        key: "location",
        icon: MapPin,
        show: !!job.location,
        primary: () => job.location,
      },
    ];

    const isPaymentPending =
      job.raw?.payment?.method === "PAY_AFTER_SERVICE" &&
      job.raw?.payment?.status !== "paid";

    return (
      <div
        onClick={() => {
          // Route package jobs to /my-jobs/package/:id, service to /my-jobs/:id
          if (isPackage) {
            navigate(`/my-jobs/package/${job.id}`);
          } else {
            navigate(`/my-jobs/service/${job.id}`);
          }
        }}
        className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow lg:flex lg:flex-row cursor-pointer"
      >
        <div className="flex-1 flex flex-col gap-4 p-3.5 sm:p-4 text-sm sm:text-base md:text-lg">
          {/* Card Header */}
          <div className="border-b border-gray-100 pb-2">
            <div className="flex justify-between items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="font-semibold text-[1em] text-primaryTextColor truncate">
                    {job.customerName}
                  </h2>
                  {/* Package badge */}
                  {isPackage && (
                    <span className="inline-flex items-center gap-1 text-[10px] px-2 rounded-full bg-purple-50 text-purple-700 border border-purple-200 font-semibold whitespace-nowrap">
                      <Package size={10} />
                      Package
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs mt-1 text-secondaryTextColor">
                  {job.phoneNumber && (
                    <div className="flex items-center gap-2">
                      <span>{job.phoneNumber}</span>
                      <span>•</span>
                      <a
                        href={`tel:${job.phoneNumber}`}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-0.5 text-primaryBtnColor font-medium active:opacity-70"
                      >
                        <Phone size={13} />
                        Call
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <div className="items-start flex gap-2">
                <span
                  className={`text-xs px-2 sm:px-3 py-1 sm:py-1.5 w-fit rounded-full border font-medium whitespace-nowrap ${config?.color}`}
                >
                  {config.label}
                </span>
                {showThreeDot && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedJob(job);
                      setShowActionSheet(true);
                    }}
                    className="p-2 rounded-full bg-gray-100 active:bg-gray-200 outline-none"
                  >
                    <MoreVertical size={15} className="text-gray-500" />
                  </button>
                )}
              </div>
            </div>
            <div className="ml-2 flex justify-between items-center">
              {isPaymentPending ? (
                <span className="text-[10px] mt-2 inline-block px-2 rounded-full bg-red-50 text-red-600 border border-red-200 font-semibold">
                  Payment Pending
                </span>
              ) : (
                <span className="text-[10px] mt-2 inline-block px-2 rounded-full bg-green-50 text-green-600 border border-green-200 font-semibold">
                  Paid
                </span>
              )}
              <span className="text-lg font-semibold text-primaryColor">
                ₹{job.totalAmount}
              </span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row">
            {/* Card Body */}
            <div className="flex flex-col gap-y-4 md:gap-y-5 flex-1">
              {jobInfoItems
                .filter((item) => item.show)
                .map(({ key, icon: Icon, primary, secondary }) => (
                  <div key={key} className="flex items-center gap-2.5 text-sm">
                    <div className="flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 flex-shrink-0">
                      <Icon className="size-4 text-gray-600" />
                    </div>
                    <div className="font-medium text-gray-900 flex flex-col leading-tight">
                      <span className="text-sm line-clamp-1">{primary(job)}</span>
                      {secondary && secondary(job) && (
                        <span className="text-xs lg:text-[0.8em] mt-0.5 text-secondaryTextColor">
                          {secondary(job)}
                        </span>
                      )}
                      {key === "location" && job.mapLink && (
                        <a
                          href={job.mapLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs mt-0.5 text-primaryBtnColor font-semibold"
                        >
                          Navigate →
                        </a>
                      )}
                    </div>
                  </div>
                ))}
            </div>

            {/* Action Button */}
            {config?.action && (
              <div className="flex-1 w-full pt-6 sm:pt-0 flex lg:items-center justify-end sm:self-end">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAction(job.id, job.status, job.bookingType);
                  }}
                  className={`h-10 sm:h-12 w-full sm:w-40 px-6 flex items-center justify-center rounded-xl text-white text-sm font-semibold transition-colors whitespace-nowrap active:scale-[0.98] ${config.btnColor}`}
                >
                  {updatingId === job.id ? (
                    <div className="flex items-center gap-1">
                      <CgSpinner className="animate-spin" size={18} />
                      Updating…
                    </div>
                  ) : (
                    config.action
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 no-scrollbar">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-gradient-to-r from-primaryColor/10 to-accentTextColor/10 backdrop-blur-md border-b border-primaryColor/20 shadow-sm">
        <div className="px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between">
            <div className="leading-tight">
              <h1 className="text-base md:text-xl font-bold text-primaryTextColor">
                My Jobs
              </h1>
              <p className="text-xs md:text-sm text-secondaryTextColor mt-0.5">
                Assigned & ongoing today
              </p>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-white shadow-md border border-gray-200 text-secondaryTextColor text-xs md:text-sm">
              <Calendar size={14} className="md:w-4 md:h-4" />
              <span className="hidden sm:inline">{todayDate}</span>
              <span className="sm:hidden font-medium">
                {new Date().toLocaleDateString("en-US", { day: "numeric", month: "short" })}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Job Cards */}
      <div className="px-4 py-4 sm:py-6 md:py-8 pb-20">
        {!loading && bookings.length === 0 ? (
          <div className="text-center py-12 lg:col-span-2">
            <div className="text-gray-400 mb-2">
              <Calendar size={48} className="mx-auto" />
            </div>
            <p className="text-gray-600 font-medium">No jobs found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <JobCardSkeleton key={i} />)
              : bookings.map((job, index) => <JobCard key={job.id || index} job={job} />)}
          </div>
        )}
      </div>

      {/* Modals */}
      <JobActionModal
        isOpen={showActionSheet}
        job={selectedJob}
        onClose={() => setShowActionSheet(false)}
        undoStartWork={undoStartWork}
        showUndoOption={canUndoOngoing}
        onReschedule={() => {
          setShowActionSheet(false);
          setShowRescheduleModal(true);
        }}
        onCancel={() => {
          setShowActionSheet(false);
          setShowCancelConfirm(true);
        }}
      />

      {showRescheduleModal && selectedJob && (
        <RescheduleBookingModal
          branchId={selectedJob?.branchId}
          booking={selectedJob?.raw}
          bookingType={selectedJob?.bookingType}
          onClose={() => setShowRescheduleModal(false)}
          onSuccess={(updatedBooking) => handleRescheduleSuccess(updatedBooking)}
        />
      )}

      <ConfirmCancelModal
        isOpen={showCancelConfirm}
        job={selectedJob}
        isCancelling={isCancelling}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={(reason) =>
          handleCancelJob(selectedJob.id, reason, selectedJob?.branchId)
        }
      />

      <VerifyOTPModal
        isOpen={showOTPModal}
        bookingId={otpJob?.id}
        onClose={() => { setShowOTPModal(false); setOtpJob(null); }}
        onVerified={continueStartWork}
      />
    </div>
  );
};

const JobCardSkeleton = () => (
  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm animate-pulse">
    <div className="p-3.5 sm:p-4 space-y-4">
      <div className="flex justify-between items-start gap-3 border-b pb-3">
        <div className="flex-1 space-y-2">
          <div className="h-4 w-40 bg-gray-200 rounded" />
          <div className="h-3 w-28 bg-gray-200 rounded" />
        </div>
        <div className="h-6 w-20 bg-gray-200 rounded-full" />
      </div>
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gray-200 rounded-full" />
            <div className="flex-1 space-y-1">
              <div className="h-3 w-32 bg-gray-200 rounded" />
              <div className="h-3 w-20 bg-gray-200 rounded" />
            </div>
          </div>
        ))}
      </div>
      <div className="pt-3 flex justify-end">
        <div className="h-10 sm:h-12 w-full sm:w-40 bg-gray-200 rounded-xl" />
      </div>
    </div>
  </div>
);

export default StaffMyJobs;
